import React, { useEffect } from 'react';
import classNames from 'classnames';
import { Spinner } from '@components';
import { ImageApi } from '@api';
import { Image as ImageUtil, LocalStorage } from '@utils';
import { Samples as SamplesType, ApiResponseImageUpdateType } from '@types';

import GlobalCtx from '@components/globalContext';

const Samples = (): JSX.Element => {
  const { app, samples, setSamples, image, setImage } = React.useContext(GlobalCtx);
  const [error, setError] = React.useState<boolean>(false);
  const [isLoading, setIsLoading] = React.useState<boolean>(samples === undefined);

  const [selected, setSelected] = React.useState<SamplesType['selected']>(undefined);

  const setIsImporting = (isImporting: boolean) => {
    setImage({
      ...image,
      isImporting,
    });
  };

  const importSelected = async () => {
    setIsImporting(true);
    try {
      let resp: ApiResponseImageUpdateType;
      if (samples.src === 'STATIC') {
        resp = await ImageApi.importStaticSample({
          clientId: app.clientId,
          imageId: selected.id,
        });
      } else {
        resp = await ImageApi.importSample({
          clientId: app.clientId,
          imageUrl: selected.url,
          filename: `${selected.id}.jpg`,
        });
      }

      setImage({
        ...image,
        imageContent: resp,
        isImporting: false,
      });
      LocalStorage.updateImage(resp);
    } catch {
      setError(true);
      setIsImporting(false);
    }
  };

  useEffect(() => {
    if (samples?.items && samples.items.length === 0) {
      ImageApi.getSamples()
        .then((resp) => {
          const formatted = ImageUtil.formatSamples(resp.images);
          setSamples({
            ...samples,
            items: formatted,
          });
          LocalStorage.updateSamples(formatted);
        })
        .finally(() => {
          setIsLoading(false);
        })
        .catch(() => {
          setError(true);
        });
    }
  }, []);

  useEffect(() => {
    if (selected) {
      importSelected();
    }
  }, [selected]);

  const sampleItems = () => {
    const items = [];
    const { selected: selectedSample } = samples;
    samples.items.forEach((item: any, index: number) => {
      const { id, urls } = item;
      items.push(
        <div
          style={{ backgroundImage: `url(${item.urls.thumb})` }}
          onClick={() => {
            setSelected({
              id,
              url: urls.full,
            });
          }}
          key={`sample--${id}`}
          title="Pick me!"
          className={classNames(
            `sample-index__${index}`,
            'lg:min-w-[200px] md:min-w-[150px] min-w-fit',
            'transition-all cursor-pointer opacity-80 h-40 m-1 bg-cover bg-center outline outline-2 outline-offset-2 border border-sky-400 shadow-sm outline-sky-200 p-2 rounded',
            'hover:outline-sky-500 hover:opacity-100',
            {
              'sample--selected': selectedSample === id,
            },
          )}
        />,
      );
    });
    return <div className="grid grid-cols-2 md:grid-cols-3 gap-1 md:gap-4">{items}</div>;
  };

  if (isLoading) {
    return <Spinner className="place-content-center flex mt-3 py-10" />;
  } else if (error) {
    return (
      <div className="text-center p-2 border-2 border-red-500 bg-red-50 text-red-500 rounded min-h-[300px] flex place-items-center place-content-center">
        <div>
          <h3 className="font-medium text-red-600 text-lg">
            Uh oh! There's normally samples here to choose from.
          </h3>
          <p>Not to worry. Our incident response team is on it :) Try again later.</p>
        </div>
      </div>
    );
  }
  return <div>{sampleItems()}</div>;
};

export default Samples;
